import {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { Combobox } from '@base-ui/react/combobox';
import { defaultRangeExtractor, Range, useVirtualizer } from '@tanstack/react-virtual';

import { isAddOptionItem } from './add-option';
import { Item, LIST_PADDING, Scroller, VirtualRows } from './styles';
import { estimateOptionHeight, measurePositionedElement, OptionVirtualizer } from './virtualized';

type VirtualizableOption = {
  value: string;
  helpText?: string;
  isDisabled?: boolean;
};

type VirtualizedOptionsProps<TOption extends VirtualizableOption> = {
  virtualizerRef: MutableRefObject<OptionVirtualizer | null>;
  highlightedIndexRef: MutableRefObject<number>;
  renderOption: (item: TOption) => ReactNode;
};

// base-ui's `virtualized` flag expects an external virtualizer — it doesn't
// virtualize itself. Keyboard nav past the rendered window is handled by the
// Root's onItemHighlighted -> scrollHighlightIntoView.
export const VirtualizedOptions = <TOption extends VirtualizableOption>({
  virtualizerRef,
  highlightedIndexRef,
  renderOption,
}: VirtualizedOptionsProps<TOption>) => {
  const filteredItems = Combobox.useFilteredItems<TOption>();
  // The add-option row is a filtered item too, but it renders as a fixed footer
  // below the Scroller rather than as a virtual row.
  const items = useMemo(
    () => filteredItems.filter((item) => !isAddOptionItem(item)),
    [filteredItems],
  );
  const hasTrailingRow = items.length < filteredItems.length;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const getItemKey = useCallback((index: number) => items[index].value, [items]);
  // base-ui commits Enter by clicking the highlighted row's mounted element, so
  // that row has to stay mounted when the user scrolls away from it.
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range);
      const highlightedIndex = highlightedIndexRef.current;
      if (
        highlightedIndex < 0 ||
        highlightedIndex >= range.count ||
        indexes.includes(highlightedIndex)
      ) {
        return indexes;
      }
      return [...indexes, highlightedIndex].sort((a, b) => a - b);
    },
    [highlightedIndexRef],
  );

  const virtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: items.length,
    getItemKey,
    rangeExtractor,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => estimateOptionHeight(items[index]),
    measureElement: measurePositionedElement,
    overscan: 20,
    paddingStart: LIST_PADDING,
    paddingEnd: hasTrailingRow ? 0 : LIST_PADDING,
  });

  useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer]);

  const setScrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (node) virtualizer.measure();
    },
    [virtualizer],
  );

  if (items.length === 0) return null;

  const totalSize = virtualizer.getTotalSize();

  return (
    <Scroller ref={setScrollRef} style={{ '--total-size': `${totalSize}px` } as CSSProperties}>
      <VirtualRows style={{ height: totalSize }}>
        {virtualizer.getVirtualItems().map((row) => {
          const item = items[row.index];
          return (
            <Item
              key={item.value}
              ref={virtualizer.measureElement}
              data-index={row.index}
              index={row.index}
              value={item}
              disabled={item.isDisabled}
              aria-setsize={filteredItems.length}
              aria-posinset={row.index + 1}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${row.start}px)`,
              }}
            >
              {renderOption(item)}
            </Item>
          );
        })}
      </VirtualRows>
    </Scroller>
  );
};
